import { bgnToEur, CurrencyCode } from '@/plugins/currencyFormatters'
import { useRootStore } from './../../../store/root'

export const statuses = {
  PAID: 'none',
  PAID_LATE: 'none',
  DEBT: 'error',
  SCHEDULED: 'none',
  CURRENT: 'success'
}

const icons = {
  PAID: 'ui-check-circle-outline',
  PAID_LATE: 'ui-check-circle-outline',
  DEBT: 'ui-check-circle-outline',
  SCHEDULED: 'ui-clock-outline',
  CURRENT: 'ui-clock-outline'
}

const colors = {
  PAID: 'mint',
  PAID_LATE: 'mint',
  CURRENT: undefined,
  DEBT: 'red',
  SCHEDULED: undefined
}

export const CALCULATION_AMOUNT_KEY = {
  PRINCIPAL: 'principal',
  INTEREST: 'interest',
  FEES: 'fees',
  TOTAL: 'total'
}

export const CALCULATION_AMOUNT_KEY_VALUES = Object.values(CALCULATION_AMOUNT_KEY)

export const PRIMARY_AMOUNT_INDEX = 0
export const SECONDARY_AMOUNT_INDEX = 1
export const AMOUNT_KEY_INDEX = 2

export const getDualCurrencyAmounts = (amount, amountKey) => {
  const amounts = []

  amounts[PRIMARY_AMOUNT_INDEX] = amount
  amounts[SECONDARY_AMOUNT_INDEX] = bgnToEur(amount)
  amounts[AMOUNT_KEY_INDEX] = amountKey // this is used to determine which column is the input for bb-transactions' itemAmount template

  return amounts
}

const mapCalculatedValues = (principal, interest, fees, total, date, currencyCode) => {
  const rootStore = useRootStore()
  const showBgMultiCurrencyValues = rootStore.isBgMultiCurrencyActive && currencyCode === CurrencyCode.BGN

  if (showBgMultiCurrencyValues) {
    return {
      [CALCULATION_AMOUNT_KEY.PRINCIPAL]: getDualCurrencyAmounts(principal, CALCULATION_AMOUNT_KEY.PRINCIPAL),
      [CALCULATION_AMOUNT_KEY.INTEREST]: getDualCurrencyAmounts(interest, CALCULATION_AMOUNT_KEY.INTEREST),
      [CALCULATION_AMOUNT_KEY.FEES]: getDualCurrencyAmounts(fees, CALCULATION_AMOUNT_KEY.FEES),
      [CALCULATION_AMOUNT_KEY.TOTAL]: getDualCurrencyAmounts(total, CALCULATION_AMOUNT_KEY.TOTAL),
      date
    }
  }
  return {
    [CALCULATION_AMOUNT_KEY.PRINCIPAL]: principal,
    [CALCULATION_AMOUNT_KEY.INTEREST]: interest,
    [CALCULATION_AMOUNT_KEY.FEES]: fees,
    [CALCULATION_AMOUNT_KEY.TOTAL]: total,
    date
  }
}

const calculateValues = (item) => {
  switch (item.statusCode) {
    case 'PAID':
    case 'PAID_LATE':
      return mapCalculatedValues(
        item.actualPayments.principal.amountPaid,
        item.actualPayments.interest.amountPaid,
        item.actualPayments.feesTotal.amountPaid,
        item.actualPayments.total.amountPaid,
        item.actualPayments.total.lastPaymentDate,
        item.currencyCode
      )
    case 'DEBT':
      return mapCalculatedValues(
        item.actualPayments.principal.amountDue,
        item.actualPayments.interest.amountDue,
        item.actualPayments.feesTotal.amountDue,
        item.actualPayments.total.amountDue,
        item.paymentDate || item.scheduleDate,
        item.currencyCode
      )
    case 'SCHEDULED':
    case 'CURRENT':
      return mapCalculatedValues(
        item.actualPayments.principal.amountDue + (item.isProcessed ? 0 : item.scheduledPayments.principal),
        item.actualPayments.interest.amountDue + (item.isProcessed ? 0 : item.scheduledPayments.interest),
        item.actualPayments.feesTotal.amountDue + (item.isProcessed ? 0 : item.scheduledPayments.feesTotal),
        item.actualPayments.total.amountDue + (item.isProcessed ? 0 : item.scheduledPayments.total),
        item.paymentDate || item.scheduleDate,
        item.currencyCode
      )
  }
}

/**
 * It is assumed that the currency is the same throughout a single year:
 * the calculated values are always either same length arrays or single numeric values.
 * There should be no situation where the existing calculation value is a number and the new calculation value is an array or vice versa.
 */
const mergeCalculationValues = (existingCalculations, newCalculationValues) => {
  for (const calculationValueKey of CALCULATION_AMOUNT_KEY_VALUES) {
    const existingCalculationValue = existingCalculations[calculationValueKey]

    if (Array.isArray(existingCalculationValue)) {
      const newCalculationValueArray = newCalculationValues[calculationValueKey]

      existingCalculationValue[PRIMARY_AMOUNT_INDEX] += newCalculationValueArray[PRIMARY_AMOUNT_INDEX]
      existingCalculationValue[SECONDARY_AMOUNT_INDEX] += newCalculationValueArray[SECONDARY_AMOUNT_INDEX]
    } else {
      existingCalculations[calculationValueKey] += newCalculationValues[calculationValueKey]
    }
  }
}

export const mapSingleItem = (item, context) => {
  const calculations = calculateValues(item)
  const button = {
    label: context.translations.buttonLabels[item.statusCode],
    visible: true
  }

  return {
    id: item.id,
    calculations,
    status: statuses[item.statusCode],
    icon: icons[item.statusCode],
    iconColor: colors[item.statusCode],
    currency: item.currencyCode,
    statusColumn: {
      progress: !context.hasProgress ? null : item.scheduleNr,
      status: context.translations.scheduleTypes[item.statusCode]
    },
    middleColumns: [
      calculations[CALCULATION_AMOUNT_KEY.PRINCIPAL],
      calculations[CALCULATION_AMOUNT_KEY.INTEREST],
      calculations[CALCULATION_AMOUNT_KEY.FEES]
    ],
    lastColumn: {
      date: new Date(calculations.date).toLocaleDateString(context.locale),
      amount: calculations[CALCULATION_AMOUNT_KEY.TOTAL]
    },
    button
  }
}

export const mapByYear = (items) => {
  const yearMap = items.reduce((years, item) => {
    const { previous, calculations } = years[item.year] || { calculations: mapCalculatedValues(0, 0, 0, 0, null, item.currencyCode) }

    const newCalculations = calculateValues(item)

    mergeCalculationValues(calculations, newCalculations)

    years[item.year] = {
      previous: item,
      calculations,
      status: statuses[(previous && ['DEBT', 'CURRENT'].includes(previous.statusCode) ? previous : item).statusCode],
      icon: icons[item.statusCode],
      iconColor: colors[item.statusCode],
      currency: item.currencyCode,
      statusColumn: {
        status: item.year
      },
      middleColumns: [
        calculations.principal,
        calculations.interest,
        calculations.fees
      ],
      lastColumn: {
        amount: calculations.total
      }
    }

    return years
  }, {})

  return Object.values(yearMap)
}
